/**
 * Selection chrome, drawn in SCREEN space on top of the scene.
 *
 * Nothing here is part of the document, so none of it appears in an export.
 */

import {
  RESIZE_HANDLES,
  handleWorldPoint,
  rotateHandleWorld,
  selectionBounds,
  selectionCorners,
  supportsBoxHandles,
} from '../geometry/hit';
import { worldBounds, type Rect, type Vec } from '../geometry/transform';
import type { BoardView, SlateElement } from '../scene/types';
import { isArrow } from '../scene/types';
import { worldToScreen } from '../scene/view';
import type { Peer } from '../collab/presence';

export interface OverlayOptions {
  readonly view: BoardView;
  readonly pixelRatio: number;
  readonly selected: readonly SlateElement[];
  readonly marquee: Rect | null;
  readonly bindTarget: SlateElement | null;
  readonly peers: readonly Peer[];
  readonly colors: {
    readonly selection: string;
    readonly surface: string;
    readonly accentSoft: string;
  };
}

const HANDLE_SIZE = 8;

function screenRectPath(ctx: CanvasRenderingContext2D, corners: readonly Vec[], view: BoardView): void {
  ctx.beginPath();
  corners.forEach((corner, index) => {
    const p = worldToScreen(view, corner);
    if (index === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.closePath();
}

function drawHandle(ctx: CanvasRenderingContext2D, p: Vec, colors: OverlayOptions['colors']): void {
  ctx.beginPath();
  ctx.rect(p.x - HANDLE_SIZE / 2, p.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
  ctx.fillStyle = colors.surface;
  ctx.fill();
  ctx.strokeStyle = colors.selection;
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

export function renderOverlay(ctx: CanvasRenderingContext2D, opts: OverlayOptions): void {
  const { view, pixelRatio, colors } = opts;
  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';

  if (opts.bindTarget) {
    const corners = selectionCorners(opts.bindTarget);
    screenRectPath(ctx, corners, view);
    ctx.strokeStyle = colors.selection;
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    ctx.globalAlpha = 0.9;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  if (opts.marquee) {
    const a = worldToScreen(view, { x: opts.marquee.x, y: opts.marquee.y });
    const b = worldToScreen(view, {
      x: opts.marquee.x + opts.marquee.width,
      y: opts.marquee.y + opts.marquee.height,
    });
    ctx.fillStyle = colors.accentSoft;
    ctx.globalAlpha = 0.45;
    ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = colors.selection;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
    ctx.setLineDash([]);
  }

  for (const el of opts.selected) {
    screenRectPath(ctx, selectionCorners(el), view);
    ctx.strokeStyle = colors.selection;
    ctx.lineWidth = 1.5;
    ctx.setLineDash(opts.selected.length > 1 ? [5, 3] : []);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  if (opts.selected.length === 1) {
    const el = opts.selected[0]!;
    if (isArrow(el)) {
      // Arrows are edited by their endpoints, not a bounding box.
      for (const point of el.points) {
        const world = { x: point[0], y: point[1] };
        const rotated = selectionCornersPoint(el, world);
        drawHandle(ctx, worldToScreen(view, rotated), colors);
      }
    } else if (supportsBoxHandles(el)) {
      for (const handle of RESIZE_HANDLES) {
        drawHandle(ctx, worldToScreen(view, handleWorldPoint(handle, el)), colors);
      }
      const rotate = worldToScreen(view, rotateHandleWorld(el, view.zoom));
      const top = worldToScreen(view, handleWorldPoint('n', el));
      ctx.beginPath();
      ctx.moveTo(top.x, top.y);
      ctx.lineTo(rotate.x, rotate.y);
      ctx.strokeStyle = colors.selection;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(rotate.x, rotate.y, HANDLE_SIZE / 2 + 1, 0, Math.PI * 2);
      ctx.fillStyle = colors.surface;
      ctx.fill();
      ctx.strokeStyle = colors.selection;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  } else if (opts.selected.length > 1) {
    const bounds = selectionBounds(opts.selected);
    if (bounds) {
      const a = worldToScreen(view, { x: bounds.x, y: bounds.y });
      const b = worldToScreen(view, { x: bounds.x + bounds.width, y: bounds.y + bounds.height });
      ctx.strokeStyle = colors.selection;
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 3]);
      ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
      ctx.setLineDash([]);
    }
  }

  for (const peer of opts.peers) {
    const p = worldToScreen(view, { x: peer.x, y: peer.y });
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 11, p.y + 4);
    ctx.lineTo(p.x + 4.5, p.y + 5.5);
    ctx.lineTo(p.x + 4, p.y + 12);
    ctx.closePath();
    ctx.fillStyle = peer.color;
    ctx.fill();
    ctx.strokeStyle = colors.surface;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

/** Local point of a linear element mapped through its own rotation. */
function selectionCornersPoint(el: SlateElement, local: Vec): Vec {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const dx = local.x - el.width / 2;
  const dy = local.y - el.height / 2;
  if (el.angle === 0) return { x: cx + dx, y: cy + dy };
  const cos = Math.cos(el.angle);
  const sin = Math.sin(el.angle);
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

export function elementWorldRect(el: SlateElement): Rect {
  return worldBounds(el);
}
